type Ctor<T> = new (...args: any[]) => T;

const FORM_EVENT_HANDLERS = [
  ["formdata", "_onFormData"],
  ["submitend", "_onSubmitEnd"],
  ["reset", "_onFormReset"],
] as const;

interface WithCEHooks {
  prototype: {
    connectedCallback?: () => void;
    disconnectedCallback?: () => void;
  };
}

const REFLECTED_ATTRS = [
  "formaction",
  "formmethod",
  "formenctype",
  "formnovalidate",
  "formtarget",
  "name",
  "value",
  "disabled",
] as const;

/** Create and append a hidden submit button for form submission proxying. */
const createHiddenSubmitter = (parent: HTMLElement): HTMLButtonElement => {
  const b = document.createElement("button");
  b.type = "submit";
  b.hidden = true;
  Object.assign(b.style, {
    position: "absolute",
    width: "0",
    height: "0",
    overflow: "hidden",
    clipPath: "inset(50%)",
  });
  parent.append(b);
  return b;
};

/** Walk up the DOM (crossing shadow boundaries) to check if `node` is inside `form`. */
const isInsideForm = (node: Node, form: HTMLFormElement): boolean => {
  let n: Node | null = node;
  while (n) {
    if (n === form) return true;
    const as = (n as any).assignedSlot as HTMLSlotElement | null;
    n = as ?? n.parentNode ?? ((n as any).host as Node | null) ?? null;
  }
  return false;
};

/** Copy relevant attributes from host to the hidden submitter. */
const reflectAttrsToSubmitter = (host: HTMLElement, submitter: HTMLButtonElement): void => {
  for (const a of REFLECTED_ATTRS) {
    const v = host.getAttribute(a);
    if (v === null) {
      submitter.removeAttribute(a);
    } else {
      submitter.setAttribute(a, v);
    }
  }
  submitter.type = "submit";
};

/** Bind form-level event listeners (submitend, reset, formdata). */
const bindFormListeners = (instance: any, form: HTMLFormElement, submitter: HTMLButtonElement | undefined): void => {
  instance._onSubmitEnd = () => dispatchLoadingEvent(instance, false);
  form.addEventListener("submitend", instance._onSubmitEnd);

  instance._onFormReset = () => dispatchLoadingEvent(instance, false);
  form.addEventListener("reset", instance._onFormReset);

  if (!submitter) return;

  if (isInsideForm(submitter, form)) {
    submitter.removeAttribute("form");
  } else {
    if (!form.id) form.id = `f_${crypto.randomUUID()}`;
    submitter.setAttribute("form", form.id);
  }

  instance._onFormData = (ev: FormDataEvent) => {
    const evSubmitter = (ev as any).submitter as HTMLElement | undefined;
    if (evSubmitter !== submitter) return;
    const name = instance.getAttribute("name");
    if (name) ev.formData.append(name, instance.getAttribute("value") ?? "");
  };
  form.addEventListener("formdata", instance._onFormData);
};

/**
 * Form-associated mixin that works with Turbo and shadow DOM.
 *
 * Automatically sets loading state during form submission:
 * - Sets loading=true when form submit is triggered
 * - Clears loading when form dispatches 'submitend' event or resets
 *
 * For async form handlers, dispatch 'submitend' on the form when complete:
 * @example
 * ```js
 * form.addEventListener('submit', async (e) => {
 *   e.preventDefault();
 *   try {
 *     await fetch('/api/submit', { method: 'POST', body: new FormData(e.target) });
 *   } finally {
 *     e.target.dispatchEvent(new Event('submitend'));
 *   }
 * });
 * ```
 */
/** Resolve element type from the `type` attribute. */
const getElementType = (el: HTMLElement): string => {
  return (el.getAttribute("type") || "").toLowerCase();
};

/** Check if element is effectively disabled. */
const isDisabledLike = (el: HTMLElement): boolean => {
  return el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true";
};

/** Dispatch loading state event. */
const dispatchLoadingEvent = (el: HTMLElement, loading: boolean): void => {
  el.dispatchEvent(new CustomEvent("update:loading", { detail: [loading], bubbles: false, composed: false }));
};

/** Tear down the hidden submitter and its form listeners. */
const teardownSubmitter = (instance: any): void => {
  if (instance._ldSubmitter) {
    instance._ldSubmitter.remove();
    instance._ldSubmitter = undefined;
  }
  if (instance._lastForm && instance._onFormData) {
    instance._lastForm.removeEventListener("formdata", instance._onFormData);
    instance._onFormData = undefined;
  }
};

/** Ensure a hidden submitter exists for submit-type elements. */
const ensureSubmitter = (instance: any): void => {
  if (getElementType(instance) !== "submit") {
    teardownSubmitter(instance);
    return;
  }
  if (instance._ldSubmitter) return;
  instance._ldSubmitter = createHiddenSubmitter(instance);
  reflectAttrsToSubmitter(instance, instance._ldSubmitter);
};

/** Handle click/keydown activation. */
const activateSubmitter = (instance: any): void => {
  if (isDisabledLike(instance)) return;
  const form = instance._internals.form;
  if (!form) return;
  if (getElementType(instance) === "reset") {
    form.reset();
    return;
  }
  if (getElementType(instance) !== "submit") return;
  reflectAttrsToSubmitter(instance, instance._ldSubmitter!);
  dispatchLoadingEvent(instance, true);
  if (instance._ldSubmitter) form.requestSubmit(instance._ldSubmitter);
};

/** Handle formAssociatedCallback. */
const handleFormAssociated = (instance: any, form: HTMLFormElement | null): void => {
  if (getElementType(instance) !== "submit") return;
  if (instance._lastForm) {
    for (const [event, handler] of FORM_EVENT_HANDLERS) {
      if (instance[handler]) {
        instance._lastForm!.removeEventListener(event, instance[handler] as EventListener);
        instance[handler] = undefined;
      }
    }
  }
  instance._lastForm = form;
  if (form) bindFormListeners(instance, form, instance._ldSubmitter);
  else if (instance._ldSubmitter) instance._ldSubmitter.removeAttribute("form");
};

export const withFormSubmitter = <TBase extends Ctor<HTMLElement> & WithCEHooks>(Base: TBase): TBase => {
  class EButtonSubmitter extends Base {
    static readonly formAssociated = true;
    readonly _internals!: ElementInternals;
    _ldSubmitter?: HTMLButtonElement;
    _attrsMO?: MutationObserver;
    _lastForm: HTMLFormElement | null = null;
    _onFormData?: (e: FormDataEvent) => void;
    _onSubmitEnd?: () => void;
    _onFormReset?: () => void;

    constructor(...args: any[]) {
      super(...(args as any));
      this._internals = (this as any).attachInternals();
      this.addEventListener("click", () => activateSubmitter(this));
      this.addEventListener("keydown", (e: KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && !isDisabledLike(this)) {
          e.preventDefault();
          activateSubmitter(this);
        }
      });
    }

    connectedCallback() {
      const superConnected = (Base.prototype as any).connectedCallback;
      if (typeof superConnected === "function") superConnected.call(this);
      ensureSubmitter(this);
      this._attrsMO = new MutationObserver((muts) => {
        if (muts.some((m) => m.attributeName === "type")) ensureSubmitter(this);
        if (getElementType(this) === "submit") reflectAttrsToSubmitter(this, this._ldSubmitter!);
      });
      this._attrsMO.observe(this, { attributes: true });
    }

    disconnectedCallback() {
      const superDisconnected = (Base.prototype as any).disconnectedCallback;
      if (typeof superDisconnected === "function") superDisconnected.call(this);
      this._attrsMO?.disconnect();
      this._attrsMO = undefined;
      teardownSubmitter(this);
    }

    formAssociatedCallback(form: HTMLFormElement | null) {
      handleFormAssociated(this, form);
    }
  }
  return EButtonSubmitter as unknown as TBase;
};
